import React from "react";
import { useNavigate } from "react-router-dom";
import FloatingCard from "../../ui/FloatingCard";

/**
 * Team Members Tab Component
 * Displays members in starters and substitutes sections
 */
const TeamMembersTab = ({
  members,
  canManageMembers,
  isChangingMemberType,
  getActiveMembersByType,
  onSwapMember,
  onRemoveMember,
  onToggleScorer,
  ownerId,
  onTransferOwnership,
}) => {
  const navigate = useNavigate();
  // Filter to show only active members
  const primaryMembers = members.filter((m) => m.status === "active" && (m.member_type === "primary" || !m.member_type));
  const substituteMembers = members.filter((m) => m.status === "active" && m.member_type === "substitute");

  const roleConfig = {
    owner:   { emoji: "👑", color: "#f59e0b", bg: "rgba(245,158,11,0.12)",  border: "rgba(245,158,11,0.3)"  },
    manager: { emoji: "👑", color: "#f59e0b", bg: "rgba(245,158,11,0.12)",  border: "rgba(245,158,11,0.3)"  },
    coach:   { emoji: "🏃‍♂️", color: "#60a5fa", bg: "rgba(96,165,250,0.12)",  border: "rgba(96,165,250,0.3)"  },
    captain: { emoji: "⭐", color: "#a78bfa", bg: "rgba(167,139,250,0.12)", border: "rgba(167,139,250,0.3)" },
    scorer:  { emoji: "🎯", color: "#34d399", bg: "rgba(52,211,153,0.12)",  border: "rgba(52,211,153,0.3)"  },
    player:  { emoji: "⚽", color: "rgba(255,255,255,0.6)", bg: "rgba(255,255,255,0.06)", border: "rgba(255,255,255,0.12)" },
  };
  const getRole = (role) => roleConfig[role] || roleConfig.player;

  const MemberCard = ({ member, memberType }) => {
    const isScorer   = member.role === "scorer";
    const isPrimary  = memberType === "primary";
    const cfg        = getRole(member.role);
    const canToggleScorer = !!onToggleScorer;

    return (
      <div style={{
        background: "linear-gradient(145deg, rgba(10,14,26,0.82) 0%, rgba(8,12,22,0.75) 100%)",
        border: "1px solid rgba(255,255,255,0.12)",
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
        borderRadius: "12px",
        padding: "12px 10px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "6px",
        textAlign: "center",
        transition: "border-color 0.2s, box-shadow 0.2s",
        minWidth: 0,
      }}>
        {/* Avatar */}
        <div style={{
          width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
          background: "rgba(255,255,255,0.07)",
          border: `2px solid ${cfg.border}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "1.1rem", overflow: "hidden",
        }}>
          {member.profile_picture || member.avatar_url
            ? <img
                src={member.profile_picture || member.avatar_url}
                alt={`${member.first_name} ${member.last_name}`}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
              />
            : null}
          {/* Initials fallback — always rendered, hidden when img loads */}
          <span
            data-testid="member-avatar-initials"
            style={{
              display: (member.profile_picture || member.avatar_url) ? "none" : "flex",
              alignItems: "center", justifyContent: "center",
              width: "100%", height: "100%",
              fontSize: "0.85rem", fontWeight: 700,
              color: cfg.color,
              background: cfg.bg,
              borderRadius: "50%",
            }}
          >
            {`${(member.first_name || "?")[0]}${(member.last_name || "")[0] || ""}`.toUpperCase()}
          </span>
        </div>

        {/* Name */}
        <div
          style={{ fontWeight: 600, fontSize: "0.78rem", color: "#b8acff", width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}
          onClick={() => navigate(`/players/${member.uuid || member.user_id}`)}
          title={`${member.first_name} ${member.last_name}`}
        >
          {member.first_name} {member.last_name}
        </div>

        {/* Role + type pills */}
        <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", justifyContent: "center" }}>
          <span style={{
            fontSize: "0.62rem", fontWeight: 600,
            padding: "1px 6px", borderRadius: "99px",
            background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.color,
          }}>
            {cfg.emoji} {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
          </span>
          <span style={{
            fontSize: "0.6rem", fontWeight: 600,
            padding: "1px 6px", borderRadius: "99px",
            background: isPrimary ? "rgba(0,255,136,0.1)" : "rgba(255,176,0,0.1)",
            border: isPrimary ? "1px solid rgba(0,255,136,0.25)" : "1px solid rgba(255,176,0,0.25)",
            color: isPrimary ? "#00ff88" : "#ffb000",
          }}>
            {isPrimary ? "STARTER" : "SUB"}
          </span>
          {isScorer && (
            <span style={{
              fontSize: "0.6rem", fontWeight: 700,
              padding: "1px 6px", borderRadius: "99px",
              background: "rgba(52,211,153,0.15)",
              border: "1px solid rgba(52,211,153,0.4)",
              color: "#34d399",
            }}>🎯</span>
          )}
        </div>

        {/* Action buttons */}
        {canManageMembers && (
          <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", justifyContent: "center", marginTop: "2px" }}>
            {/* Swap */}
            <button
              onClick={() => onSwapMember(member, isPrimary ? "substitute" : "primary")}
              title={isPrimary ? "Move to Substitutes" : "Move to Starters"}
              disabled={isChangingMemberType}
              style={{
                width: 26, height: 26, borderRadius: "6px", border: "none", cursor: "pointer",
                background: isPrimary ? "rgba(251,146,60,0.15)" : "rgba(52,211,153,0.15)",
                color: isPrimary ? "#fb923c" : "#34d399",
                fontSize: "0.85rem", display: "flex", alignItems: "center", justifyContent: "center",
                opacity: isChangingMemberType ? 0.35 : 1,
                transition: "background 0.15s",
              }}
            >🔁</button>

            {/* Remove */}
            <button
              onClick={() => onRemoveMember(member.user_id, `${member.first_name} ${member.last_name}`)}
              title="Remove from team"
              disabled={isChangingMemberType}
              style={{
                width: 26, height: 26, borderRadius: "6px", border: "none", cursor: "pointer",
                background: "rgba(239,68,68,0.12)", color: "#f87171",
                fontSize: "0.85rem", display: "flex", alignItems: "center", justifyContent: "center",
                opacity: isChangingMemberType ? 0.35 : 1, transition: "background 0.15s",
              }}
            >✕</button>

            {/* Make owner (TM-B12) — owner only, not on their own card */}
            {onTransferOwnership && Number(member.user_id) !== Number(ownerId) && (
              <button
                onClick={() => onTransferOwnership(member)}
                title="Make this member the team owner"
                aria-label={`Make ${member.first_name || ""} ${member.last_name || ""} the owner`.replace(/\s+/g, " ")}
                disabled={isChangingMemberType}
                style={{
                  width: 26, height: 26, borderRadius: "6px", border: "none", cursor: "pointer",
                  background: "rgba(245,158,11,0.15)", color: "#f59e0b",
                  fontSize: "0.85rem", display: "flex", alignItems: "center", justifyContent: "center",
                  opacity: isChangingMemberType ? 0.35 : 1, transition: "background 0.15s",
                }}
              >👑</button>
            )}

            {/* Scorer toggle */}
            {canToggleScorer && (
              <button
                onClick={() => onToggleScorer(member)}
                title={isScorer ? "Click to remove Scorer role" : "Click to assign Scorer role"}
                disabled={isChangingMemberType}
                style={{
                  height: 26, borderRadius: "6px", cursor: "pointer",
                  padding: "0 7px",
                  background: isScorer ? "rgba(239,68,68,0.15)" : "rgba(52,211,153,0.1)",
                  color: isScorer ? "#f87171" : "#34d399",
                  fontSize: "0.62rem", fontWeight: 700,
                  display: "flex", alignItems: "center", gap: "3px",
                  border: isScorer ? "1px solid rgba(239,68,68,0.35)" : "1px solid rgba(52,211,153,0.25)",
                  opacity: isChangingMemberType ? 0.35 : 1,
                  transition: "all 0.15s",
                  whiteSpace: "nowrap",
                }}
              >
                {isScorer ? "✕ S" : "🎯 S"}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const Section = ({ title, color, members: sectionMembers, memberType }) => (
    <div style={{ marginBottom: "1.5rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
        <h3 style={{ margin: 0, fontSize: "0.95rem", fontWeight: 700, color }}>
          {memberType === "primary" ? "🏃 Starting Roster" : "🔄 Substitutes"}
        </h3>
        <span style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.4)", fontWeight: 500 }}>
          ({sectionMembers.length})
        </span>
      </div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
        gap: "8px",
      }}>
        {sectionMembers.map((member) => (
          <MemberCard key={member.user_id} member={member} memberType={memberType} />
        ))}
      </div>
    </div>
  );

  if (primaryMembers.length === 0 && substituteMembers.length === 0) {
    return (
      <FloatingCard className="empty-state-gaming delay-4">
        <div className="empty-content-gaming">
          <div className="empty-icon-gaming">👥</div>
          <h3 className="empty-title-gaming">No Members Yet</h3>
          <p className="empty-text-gaming">
            This team doesn't have any members yet. Start by inviting players to join!
          </p>
        </div>
      </FloatingCard>
    );
  }

  return (
    <div className="members-content-gaming">
      {primaryMembers.length > 0 && (
        <Section title="Starting Roster" color="#00ff88" members={primaryMembers} memberType="primary" />
      )}
      {substituteMembers.length > 0 && (
        <Section title="Substitutes" color="#ffb000" members={substituteMembers} memberType="substitute" />
      )}
    </div>
  );
};

export default TeamMembersTab;
